import { ChevronRight, ExpandMore } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Children, useState } from 'react';
import type {
  IRequirementOption,
  ITargetAutocompleteProps,
} from '../../../Interfaces/ILegalDependency';
import {
  getTargetOptionLabel,
  getTargetTypeLabel,
  renderStatusChip,
} from './utils';

export const HierarchicalTargetAutocomplete = ({
  disabled,
  label,
  noOptionsText,
  onChange,
  options,
  placeholder,
  value,
}: ITargetAutocompleteProps) => {
  const [expandedPhases, setExpandedPhases] = useState<Record<string, boolean>>(
    {}
  );

  const togglePhase = (phaseKey: string) => {
    setExpandedPhases(current => ({
      ...current,
      [phaseKey]: !(current[phaseKey] ?? true),
    }));
  };

  return (
    <Autocomplete<IRequirementOption, false, false, false>
      disabled={disabled}
      filterOptions={(availableOptions, state) => {
        const query = state.inputValue.trim().toLowerCase();

        if (query.length === 0) {
          return availableOptions;
        }

        const matchedOptions = availableOptions.filter(option =>
          [
            option.legalTargetLabel,
            option.legalPhaseLabel,
            option.transactionLabel,
            getTargetTypeLabel(option.targetType),
          ]
            .join(' ')
            .toLowerCase()
            .includes(query)
        );
        const matchedPhaseIds = new Set(
          matchedOptions
            .map(option => option.idLegalPhase)
            .filter((id): id is number => id !== null)
        );

        return availableOptions.filter(option => {
          if (matchedOptions.includes(option)) {
            return true;
          }

          return (
            option.targetType === 'phase' &&
            option.idLegalPhase !== null &&
            matchedPhaseIds.has(option.idLegalPhase)
          );
        });
      }}
      getOptionLabel={getTargetOptionLabel}
      groupBy={option => option.legalPhaseLabel}
      isOptionEqualToValue={(option, currentValue) =>
        option.targetType === currentValue.targetType &&
        option.transactionId === currentValue.transactionId &&
        option.idLegalRequirement === currentValue.idLegalRequirement &&
        option.idLegalPhase === currentValue.idLegalPhase
      }
      noOptionsText={noOptionsText}
      onChange={(_, nextValue) => onChange(nextValue)}
      options={options}
      renderGroup={params => {
        const phaseOption = options.find(
          option =>
            option.targetType === 'phase' &&
            option.legalPhaseLabel === params.group
        );
        const children = Children.toArray(params.children);
        const requirementChildren = children.slice(1);
        const phaseKey = `${phaseOption?.transactionId ?? 'none'}-${params.group}`;
        const isExpanded = expandedPhases[phaseKey] ?? true;

        return (
          <Box
            component='li'
            key={params.key}
            sx={{
              listStyle: 'none',
              px: 0.75,
              py: 0.5,
            }}
          >
            {phaseOption ? (
              <Box
                onClick={() => onChange(phaseOption)}
                sx={{
                  alignItems: 'center',
                  border: theme => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  display: 'flex',
                  gap: 1,
                  px: 1.25,
                  py: 0.9,
                  bgcolor: 'background.paper',
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography fontWeight={700} variant='body2'>
                    {phaseOption.legalTargetLabel}
                  </Typography>
                  <Typography color='text.secondary' variant='caption'>
                    {phaseOption.transactionLabel}
                  </Typography>
                </Box>
                {renderStatusChip(phaseOption.currentStatusLabel)}
                {requirementChildren.length > 0 ? (
                  <IconButton
                    onClick={event => {
                      event.preventDefault();
                      event.stopPropagation();
                      togglePhase(phaseKey);
                    }}
                    size='small'
                  >
                    {isExpanded ? <ExpandMore /> : <ChevronRight />}
                  </IconButton>
                ) : null}
              </Box>
            ) : null}

            {requirementChildren.length > 0 && isExpanded ? (
              <Box
                sx={{
                  mt: 0.75,
                  ml: 2,
                  '& .MuiAutocomplete-option': {
                    alignItems: 'center',
                    borderRadius: 1.5,
                    my: 0.375,
                    px: 1.25,
                    py: 0.9,
                  },
                }}
              >
                {requirementChildren}
              </Box>
            ) : null}
          </Box>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          size='small'
        />
      )}
      renderOption={(props, option) => (
        <Box
          component='li'
          {...props}
          sx={{
            bgcolor: 'background.paper',
          }}
        >
          <Stack
            alignItems='center'
            direction='row'
            justifyContent='space-between'
            spacing={1}
            sx={{ width: '100%' }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography noWrap variant='body2'>
                {option.legalTargetLabel}
              </Typography>
              <Typography color='text.secondary' variant='caption'>
                {option.legalPhaseLabel}
              </Typography>
            </Box>
            {renderStatusChip(option.currentStatusLabel)}
          </Stack>
        </Box>
      )}
      size='small'
      value={value}
    />
  );
};
